import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { debounce } from 'lodash'
import apiClient from '@/utils/apiClient'

export function usePartSearch() {
  const router = useRouter()
  const route = useRoute()

  const MIN_LENGTH = 2
  const searchQuery = ref('')
  const searchResults = ref([])
  const searchLoading = ref(false)
  const dropdownOpen = ref(false)

  function withLocale(to) {
    const locale = route.params.locale || localStorage.getItem('locale') || 'en'
    return { ...to, params: { locale, ...to.params } }
  }

  const fetchResults = async (q) => {
    searchLoading.value = true
    try {
      const { data } = await apiClient.get('/parts', { params: { q } })
      searchResults.value = data.data
    } catch {
      searchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }

  const debouncedFetch = debounce((q) => fetchResults(q), 350)

  const onSearchInput = () => {
    const q = searchQuery.value.trim()
    if (q.length < MIN_LENGTH) {
      searchResults.value = []
      searchLoading.value = false
      debouncedFetch.cancel()
      return
    }
    dropdownOpen.value = true
    searchLoading.value = true
    debouncedFetch(q)
  }

  const onSearchFocus = () => {
    if (searchQuery.value.trim().length >= MIN_LENGTH) {
      dropdownOpen.value = true
    }
  }

  const closeDropdown = () => {
    dropdownOpen.value = false
    debouncedFetch.cancel()
  }

  const goToProduct = (part, inputRef = null) => {
    closeDropdown()
    searchQuery.value = ''
    searchResults.value = []
    inputRef?.$el?.blur()
    router.push(withLocale({ name: 'ProductPage', params: { partNumber: part.normalizedPartNumber } }))
  }

  const goToEnquiry = (q = null) => {
    const query = q ?? searchQuery.value.trim()
    closeDropdown()
    searchQuery.value = ''
    if (query) {
      router.push(withLocale({ name: 'SearchEnquiryPage', params: { partNumber: query } }))
    } else {
      router.push(withLocale({ name: 'SearchEnquiryPage' }))
    }
  }

  const onSearchEnter = async (inputRef = null) => {
    const q = searchQuery.value.trim()
    if (!q) return

    const exact = searchResults.value.find((p) => p.partNumber.toLowerCase() === q.toLowerCase())
    if (exact) {
      goToProduct(exact, inputRef)
      return
    }

    closeDropdown()
    searchQuery.value = ''
    await router.push(withLocale({ name: 'SearchEnquiryPage', params: { partNumber: q } }))
  }

  const destroy = () => {
    debouncedFetch.cancel()
  }

  return {
    MIN_LENGTH,
    searchQuery,
    searchResults,
    searchLoading,
    dropdownOpen,
    onSearchInput,
    onSearchFocus,
    closeDropdown,
    goToProduct,
    goToEnquiry,
    onSearchEnter,
    destroy,
  }
}
